/**
 * COMBAT SFX — a frozen-noir brawl kit: iron, wood, breath and weather.
 *
 * Every voice in the kit is a GENERATED recording (Mirelo text-to-audio via
 * Higgsfield, peak-normalised offline with ffmpeg) — swings, blocks, the heal,
 * and one signature sound per class ability. The synthesised voices below are
 * kept as the zero-latency fallback: a sample takes a fetch + decode to arrive,
 * and the first swing of the first bout must not be silent.
 *
 * ARCHITECTURE — every synth voice is a `render(c, out, t)` that builds its
 * graph into whatever AudioContext it is handed. `play()` feeds it the live
 * context; a test can feed it an OfflineAudioContext and measure the result,
 * which is how these were levelled (peak/RMS per voice) instead of guessed by ear.
 */

import strikeUrl from '../assets/fimbulwake/sfx/strike.mp3';
import heavyUrl from '../assets/fimbulwake/sfx/heavy.mp3';
import bodyHitUrl from '../assets/fimbulwake/sfx/body-hit.mp3';
import blockUrl from '../assets/fimbulwake/sfx/block.mp3';
import healUrl from '../assets/fimbulwake/sfx/heal.mp3';
import whooshUrl from '../assets/fimbulwake/sfx/whoosh.mp3';
import bowShotUrl from '../assets/fimbulwake/sfx/bow-shot.mp3';
import berserkUrl from '../assets/fimbulwake/sfx/berserk.mp3';
import shieldWallUrl from '../assets/fimbulwake/sfx/shield-wall.mp3';
import heartseekerUrl from '../assets/fimbulwake/sfx/heartseeker.mp3';
import witchKnotUrl from '../assets/fimbulwake/sfx/witch-knot.mp3';

let ctx: AudioContext | null = null;
// The mute preference survives reloads — before this it silently reset and
// the only toggle lived in a dead component (QA 86cb1qvpt sound pass).
let muted = (() => { try { return localStorage.getItem('fw.sfx') === 'off'; } catch { return false; } })();

export function setMuted(m: boolean) {
  muted = m;
  try { localStorage.setItem('fw.sfx', m ? 'off' : 'on'); } catch { /* private mode */ }
}
export function isMuted() { return muted; }

function ac(): AudioContext | null {
  if (muted) return null;
  try {
    ctx ??= new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch { return null; }
}

// ── building blocks (synth fallback) ────────────────────────────────────────
type Ctx = BaseAudioContext;

/** A burst of noise. Brown (integrated) reads as body and weather; white as air. */
function noiseSrc(c: Ctx, dur: number, brown = false): AudioBufferSourceNode {
  const n = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, n, c.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < n; i++) {
    const w = Math.random() * 2 - 1;
    if (brown) { last = (last + 0.02 * w) / 1.02; d[i] = last * 3.2; }
    else d[i] = w;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  return src;
}

/** Percussive envelope: near-instant attack, exponential tail. (Named `perc`,
 *  not `hit` — the public kit already owns that name for a body blow.) */
function perc(c: Ctx, t: number, peak: number, attack: number, decay: number): GainNode {
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  return g;
}

/** Swelling envelope for anything that breathes in rather than strikes. */
function swell(c: Ctx, t: number, peak: number, rise: number, hold: number, fall: number): GainNode {
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + rise);
  g.gain.setValueAtTime(Math.max(0.0002, peak), t + rise + hold);
  g.gain.exponentialRampToValueAtTime(0.0001, t + rise + hold + fall);
  return g;
}

/** Filtered noise band with a moving centre — wind, whistles, roars, scrapes. */
function band(
  c: Ctx, out: AudioNode, t: number,
  o: { dur: number; from: number; to: number; q: number; peak: number; attack: number; decay: number; brown?: boolean; type?: BiquadFilterType },
) {
  const src = noiseSrc(c, o.dur, o.brown);
  const f = c.createBiquadFilter();
  f.type = o.type ?? 'bandpass';
  f.Q.value = o.q;
  f.frequency.setValueAtTime(o.from, t);
  f.frequency.exponentialRampToValueAtTime(Math.max(20, o.to), t + o.dur * 0.9);
  src.connect(f).connect(perc(c, t, o.peak, o.attack, o.decay)).connect(out);
  src.start(t);
  src.stop(t + o.dur + 0.05);
}

/** A struck-metal partial. Iron rings inharmonically, so callers stack a few. */
function ring(
  c: Ctx, out: AudioNode, t: number,
  o: { freq: number; to?: number; peak: number; decay: number; type?: OscillatorType },
) {
  const osc = c.createOscillator();
  osc.type = o.type ?? 'triangle';
  osc.frequency.setValueAtTime(o.freq, t);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(Math.max(20, o.to), t + o.decay);
  osc.connect(perc(c, t, o.peak, 0.003, o.decay)).connect(out);
  osc.start(t);
  osc.stop(t + o.decay + 0.05);
}

/** A drum: pitched-down sine body under a noise transient. */
function drum(c: Ctx, out: AudioNode, t: number, o: { freq: number; peak: number; decay: number }) {
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(o.freq, t);
  osc.frequency.exponentialRampToValueAtTime(o.freq * 0.42, t + o.decay);
  osc.connect(perc(c, t, o.peak, 0.004, o.decay)).connect(out);
  osc.start(t);
  osc.stop(t + o.decay + 0.05);
  band(c, out, t, { dur: 0.09, from: 1400, to: 260, q: 0.7, peak: o.peak * 0.5, attack: 0.002, decay: 0.07 });
}

// ── the voices ──────────────────────────────────────────────────────────────
/** Every sound in the kit, keyed by name. */
export type SfxName =
  | 'strike' | 'heavy' | 'bodyHit' | 'block' | 'heal' | 'whoosh'
  | 'bowShot' | 'berserk' | 'shieldWall' | 'heartseeker' | 'witchKnot';

const VOICES: Record<SfxName, (c: Ctx, out: AudioNode, t: number) => void> = {
  /** A light weapon swing that connects — air, then a short iron tick. */
  strike(c, out, t) {
    band(c, out, t, { dur: 0.14, from: 900, to: 240, q: 0.8, peak: 0.5, attack: 0.004, decay: 0.11, brown: true });
    ring(c, out, t + 0.02, { freq: 430, to: 300, peak: 0.16, decay: 0.09 });
  },

  /** A two-handed blow: the body of the hit, iron shock, and the air it drags. */
  heavy(c, out, t) {
    drum(c, out, t, { freq: 96, peak: 0.85, decay: 0.26 });
    band(c, out, t, { dur: 0.3, from: 1100, to: 130, q: 0.6, peak: 0.6, attack: 0.005, decay: 0.26, brown: true });
    ring(c, out, t + 0.012, { freq: 212, to: 150, peak: 0.24, decay: 0.34 });
    ring(c, out, t + 0.012, { freq: 337, to: 250, peak: 0.14, decay: 0.22 });
  },

  /** A dull hit taken in the body — no metal, all weight. */
  bodyHit(c, out, t) {
    band(c, out, t, { dur: 0.13, from: 420, to: 90, q: 0.5, peak: 0.5, attack: 0.004, decay: 0.11, brown: true, type: 'lowpass' });
    ring(c, out, t, { freq: 74, to: 52, peak: 0.3, decay: 0.14, type: 'sine' });
  },

  /** Caught on the shield: wood knock, then the boss rings off-key. */
  block(c, out, t) {
    band(c, out, t, { dur: 0.08, from: 700, to: 300, q: 1.1, peak: 0.4, attack: 0.002, decay: 0.06, brown: true });
    ring(c, out, t, { freq: 1180, peak: 0.2, decay: 0.3 });
    ring(c, out, t + 0.004, { freq: 1637, peak: 0.13, decay: 0.24 });   // inharmonic partner
    ring(c, out, t + 0.002, { freq: 268, to: 210, peak: 0.18, decay: 0.18 });
  },

  /** Patch-up: a warm breath and an open fifth swelling under it. */
  heal(c, out, t) {
    band(c, out, t, { dur: 0.7, from: 700, to: 1900, q: 2.2, peak: 0.1, attack: 0.2, decay: 0.45 });
    const pad = swell(c, t, 0.11, 0.22, 0.1, 0.45);
    pad.connect(out);
    [293.66, 440.0, 587.33].forEach((f, i) => {           // D4 · A4 · D5
      const o = c.createOscillator();
      o.type = 'sine';
      o.frequency.value = f * (i === 1 ? 1.001 : 1);       // a hair of detune, not a chorus
      const g = c.createGain();
      g.gain.value = [1, 0.55, 0.3][i];
      o.connect(g).connect(pad);
      o.start(t);
      o.stop(t + 1.0);
    });
  },

  /** Rune-work thrown through the air. */
  whoosh(c, out, t) {
    band(c, out, t, { dur: 0.32, from: 480, to: 2400, q: 1.4, peak: 0.32, attack: 0.03, decay: 0.28 });
  },

  /** Bowstring: the creak of the draw, the snap, the shaft going away. */
  bowShot(c, out, t) {
    band(c, out, t, { dur: 0.1, from: 2600, to: 900, q: 2.5, peak: 0.34, attack: 0.002, decay: 0.08 });
    ring(c, out, t, { freq: 780, to: 320, peak: 0.12, decay: 0.07 });
    band(c, out, t + 0.05, { dur: 0.42, from: 2100, to: 520, q: 7, peak: 0.16, attack: 0.02, decay: 0.36 });
  },

  /** WARRIOR · Berserkergang — a throat opening, a war-drum under it. */
  berserk(c, out, t) {
    band(c, out, t, { dur: 0.75, from: 240, to: 620, q: 3.4, peak: 0.42, attack: 0.06, decay: 0.6, brown: true });
    band(c, out, t + 0.04, { dur: 0.6, from: 1500, to: 700, q: 2.2, peak: 0.16, attack: 0.08, decay: 0.5 });
    drum(c, out, t, { freq: 82, peak: 0.8, decay: 0.34 });
    drum(c, out, t + 0.26, { freq: 74, peak: 0.5, decay: 0.3 });
    ring(c, out, t + 0.03, { freq: 110, to: 82, peak: 0.24, decay: 0.5, type: 'sawtooth' });
  },

  /** PALADIN · Shield-Wall — boots set, wood braced, iron holding a note. */
  shieldWall(c, out, t) {
    band(c, out, t, { dur: 0.12, from: 520, to: 160, q: 0.9, peak: 0.55, attack: 0.003, decay: 0.1, brown: true });
    ring(c, out, t + 0.01, { freq: 196, to: 165, peak: 0.3, decay: 0.5 });
    ring(c, out, t + 0.01, { freq: 294, peak: 0.16, decay: 0.42 });
    const hum = swell(c, t + 0.05, 0.1, 0.12, 0.34, 0.5);
    hum.connect(out);
    [98, 147].forEach((f) => {                            // G2 + its fifth: a held wall
      const o = c.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      o.connect(hum);
      o.start(t + 0.05);
      o.stop(t + 1.1);
    });
  },

  /** ARCHER · Heartseeker — a long cold draw, one shaft, gone. */
  heartseeker(c, out, t) {
    band(c, out, t, { dur: 0.34, from: 320, to: 900, q: 6, peak: 0.14, attack: 0.16, decay: 0.16 }); // the draw
    band(c, out, t + 0.34, { dur: 0.1, from: 3000, to: 800, q: 2.2, peak: 0.44, attack: 0.002, decay: 0.08 });
    ring(c, out, t + 0.34, { freq: 900, to: 300, peak: 0.16, decay: 0.08 });
    band(c, out, t + 0.4, { dur: 0.5, from: 2600, to: 380, q: 9, peak: 0.2, attack: 0.02, decay: 0.44 }); // flight
    ring(c, out, t + 0.86, { freq: 150, to: 90, peak: 0.3, decay: 0.16, type: 'sine' });                 // it lands
  },

  /** MAGE · Witch-Knot — a knot pulled tight: rising rune, bell, snap. */
  witchKnot(c, out, t) {
    band(c, out, t, { dur: 0.5, from: 420, to: 3200, q: 3, peak: 0.24, attack: 0.05, decay: 0.42 });
    // the bell: a carrier bent by a fast modulator — glassy, not a game chime
    const car = c.createOscillator(); car.type = 'sine'; car.frequency.value = 1244.5;   // D#6, cold
    const mod = c.createOscillator(); mod.type = 'sine'; mod.frequency.value = 311.1;
    const depth = c.createGain(); depth.gain.setValueAtTime(900, t);
    depth.gain.exponentialRampToValueAtTime(20, t + 0.5);
    mod.connect(depth).connect(car.frequency);
    car.connect(perc(c, t + 0.12, 0.2, 0.006, 0.6)).connect(out);
    mod.start(t); car.start(t + 0.12);
    mod.stop(t + 0.8); car.stop(t + 0.8);
    band(c, out, t + 0.52, { dur: 0.14, from: 1800, to: 300, q: 1.2, peak: 0.3, attack: 0.002, decay: 0.12 }); // the snap
    ring(c, out, t + 0.52, { freq: 88, to: 62, peak: 0.22, decay: 0.2, type: 'sine' });
  },
};

/**
 * Per-voice trim for the SYNTH fallback, in one table because levelling by ear
 * across eleven voices is how a kit ends up with an inaudible swing next to a
 * clipping hammer. These factors were fitted from OfflineAudioContext renders;
 * re-measure after touching a voice.
 */
const TRIM: Record<SfxName, number> = {
  strike: 3.2,
  heavy: 0.8,
  bodyHit: 1.8,
  block: 1.5,
  heal: 1.5,
  whoosh: 6.0,
  bowShot: 2.6,
  berserk: 0.85,
  shieldWall: 1.4,
  heartseeker: 1.7,
  witchKnot: 1.7,
};

/**
 * Build one synth voice into any context, trimmed. Public because it is the
 * only honest way to verify these: render into an OfflineAudioContext and
 * measure peak/RMS.
 */
export function renderInto(name: SfxName, c: Ctx, out: AudioNode, at = 0) {
  const trim = c.createGain();
  trim.gain.value = TRIM[name];
  trim.connect(out);
  VOICES[name](c, trim, at);
}

// ── the samples (generated recordings, one per voice) ───────────────────────
const SAMPLE_URL: Record<SfxName, string> = {
  strike: strikeUrl,
  heavy: heavyUrl,
  bodyHit: bodyHitUrl,
  block: blockUrl,
  heal: healUrl,
  whoosh: whooshUrl,
  bowShot: bowShotUrl,
  berserk: berserkUrl,
  shieldWall: shieldWallUrl,
  heartseeker: heartseekerUrl,
  witchKnot: witchKnotUrl,
};

/** Per-sample gain. The files are peak-normalised, so this is a MIX, not a
 *  repair: abilities and heavy blows sit above the small change of a bout. */
const SAMPLE_VOL: Record<SfxName, number> = {
  strike: 0.7,
  heavy: 0.85,
  bodyHit: 0.7,
  block: 0.65,
  heal: 0.6,
  whoosh: 0.7,
  bowShot: 0.75,
  berserk: 0.9,
  shieldWall: 0.85,
  heartseeker: 0.9,
  witchKnot: 0.85,
};

const buffers: Partial<Record<SfxName, AudioBuffer | null>> = {};

function load(name: SfxName) {
  const c = ac(); if (!c || buffers[name] !== undefined) return;
  buffers[name] = null; // in-flight marker
  fetch(SAMPLE_URL[name]).then((r) => r.arrayBuffer()).then((b) => c.decodeAudioData(b))
    .then((buf) => { buffers[name] = buf; }).catch(() => { buffers[name] = null; });
}
// Warm the whole kit off the critical path — a bout can open at any moment.
setTimeout(() => { (Object.keys(SAMPLE_URL) as SfxName[]).forEach(load); }, 0);

/** Play one voice: the recording when it is decoded, the synth until then. */
function fire(name: SfxName) {
  const c = ac();
  if (!c) return;
  const buf = buffers[name];
  if (buf) {
    const src = c.createBufferSource();
    src.buffer = buf;
    const g = c.createGain();
    g.gain.value = SAMPLE_VOL[name];
    src.connect(g).connect(c.destination);
    src.start();
  } else {
    load(name);
    renderInto(name, c, c.destination, c.currentTime + 0.001);
  }
}

// ── the public kit ──────────────────────────────────────────────────────────
/** A bowstring loosed. `big` is the aimed ability shot. */
export function bowShot(big = false) { fire(big ? 'heartseeker' : 'bowShot'); }
/** Kept for older call sites — the Hunter's shot has never been a gun. */
export const gunshot = bowShot;

/** An axe swing biting home. */
export function axeSwing(big = false) { fire(big ? 'heavy' : 'strike'); }

/** Metallic parry. */
export function clash() { fire('block'); }
/** Caught on the shield. */
export function block() { fire('block'); }

/** A weapon thud. */
export function punch(big = false) { fire(big ? 'heavy' : 'strike'); }
/** A blow taken in the body. */
export function hit() { fire('bodyHit'); }
/** A crushing blow — the heavy voice, whatever the weapon. */
export function heavy() { fire('heavy'); }
/** A thrown rune. */
export function whoosh() { fire('whoosh'); }
/** Patch-up. */
export function heal() { fire('heal'); }

/** Each class's signature move has its own voice. */
export function ability(cls: string | undefined) {
  if (cls === 'WARRIOR') fire('berserk');
  else if (cls === 'PALADIN') fire('shieldWall');
  else if (cls === 'ARCHER') fire('heartseeker');
  else if (cls === 'MAGE') fire('witchKnot');
  else fire('heavy');
}

/** Route an ordinary attack by hero class + action. */
export function attackSfx(cls: string | undefined, action: string) {
  if (action === 'ability') { ability(cls); return; }
  const big = action === 'heavy';
  if (cls === 'ARCHER') bowShot(big);
  else if (cls === 'PALADIN' || cls === 'WARRIOR') axeSwing(big);
  else if (cls === 'MAGE') whoosh();
  else punch(big);
}

/**
 * End stings. The musical figures live in music.ts (they are music, and they
 * duck the bed); this stays as the compatibility door for older call sites.
 */
export function sting(win: boolean) {
  void import('./music').then((m) => (win ? m.victory() : m.defeat()));
}
